import React, { useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from "uuid";
import { Navigate } from "react-router-dom";
import {
  ArrowBack,
} from "@mui/icons-material";
import { Fab, Tooltip } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { 
  Feature,
  FeatureBase,
  IStringHash, 
  ModelBase
} from "@models";
import { 
  CreatureGift, 
  Skill,
  Tool,
  Condition,
  TemplateBase,
  Resource,
  Sense,
  TableBase,
  TableEntry,
  Attributes,
  BasicCreatureInstance,
  CreatureLimitation,
  Legendaries,
  LegendariesTable,
  SKILLS,
} from "@dndModels";
import { 
  DAMAGE_TYPES, 
  SLOT_LEVELS,
  DICE_SIZES,
  PROFICIENCY_OPTIONS,
  CHARACTER_LEVELS,
  CREATURE_TYPES,
  ABILITY_SCORES,
  CREATURE_FEATURE_TYPES,
} from "@dndModels/Constants";
import { 
  AbilityScoreType,
  CreatureType,
  DamageType,
} from "@dndModels/Types";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import StringBox from "@components/input/strings/StringBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";

import TemplateBox from "@dndComponents/TemplateBox";

import ModelBaseDetails from "@dndComponents/ModelBaseDetails";
import FloatMenu from "@components/input/menus/FloatMenu";
import LoadingBox from "@components/input/LoadingBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import DisplayObjects from "@dndComponents/display/DisplayObjects";
import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import CheckBox from "@components/input/CheckBox";
import TableInput from "@dndComponents/other/TableInput";
// import ButtonBox from '../../../../components/input/buttons/ButtonBox';
import FeatureBaseInput from "@dndComponents/feature/FeatureBaseInput";
import FeatureBasesInput from "@dndComponents/feature/FeatureBasesInput";
import CreatureSpells from "@dndComponents/creature/CreatureSpells";
import SuggestionStringBox from "@dndComponents/select/SuggestionStringBox";
import CreatureLimitInput from "@dndComponents/limitations/CreatureLimitInput";
import DamageTypeOverridesInput from "@dndComponents/creature/DamageTypeOverridesInput";
import LegendariesTableInput from "@dndComponents/other/LegendariesTableInput";
import StringsBox from '@components/input/strings/StringsBox';
import UpgradableNumberBox from '@dndComponents/UpgradableNumberBox';

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

interface Props {
  obj?: ModelBase;
}

export default function CreatureGiftEdit({ obj: param_obj }: Props) {
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [obj, setObj] = useState<CreatureGift>(param_obj instanceof CreatureGift ? param_obj : api.applyWorkingOn(new CreatureGift()));
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [tools, setTools] = useState<Tool[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [senses, setSenses] = useState<Sense[]>([]);
  const [change_time, setChangeTime] = useState(0);
  const [tab, setTab] = useState("main");
  const [expanded_feature_base, setExpandedFeatureBase] = useState<FeatureBase | null>(null);
  const [edit_attribute, setEditAttribute] = useState("");
  const [attributes_level, setAttributesLevel] = useState(-1);
  const [resource_level, setResourceLevel] = useState(-1);
  const [legendaries_level, setLegendariesLevel] = useState(-1);
  const [level_filter, setLevelFilter] = useState(-1);
  
  const disabled = api.user === null || !api.online || (!api.user.admin && obj._id !== "" && obj.owner_id !== api.user._id);

  const load = useCallback( async () => {
    const res = await api.getSetOfObjects({ data_types: ["tool","condition","spell","resource"]});
    setTools(res.tool as Tool[]);
    setConditions(res.condition as Condition[]);
    setResources(res.resource as Resource[]);
    
    const new_senses = res.sense as Sense[];
    setSenses(new_senses);
    if (obj._id !== "") {
      if (obj.legendaries_table.entries.length > 0) {
        const min = Math.min(...obj.legendaries_table.entries.map(o => o.level));
        setLegendariesLevel(min);
      }
      if (obj.resources_table.entries.length > 0) {
        const min = Math.min(...obj.resources_table.entries.map(o => o.level));
        setResourceLevel(min);
      }
      if (obj.attributes_table.entries.length > 0) {
        const min = Math.min(...obj.attributes_table.entries.map(o => o.level));
        setAttributesLevel(min);
      }
    } 
    setLoading(false);
  }, [obj]);
  
  useEffect(() => {
    if(!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  });

  useEffect(() => {
    if(reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const render_tab = () => {
    if (tab === "attributes") {
      const level_options = CHARACTER_LEVELS.map(o => +o).filter(o => o <= obj.max_level).map(o => { return `${o}`; });
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox 
              name="Select Level for Attributes"
              options={level_options}
              value={`${attributes_level}`}
              onChange={(changed: string) => {
                const new_obj = obj;
                const new_level = +changed;
                const old_entry = new_obj.attributes_table.entries.find(o => o.level === new_level);
                if (!old_entry && new_obj.attributes_table.entries.length > 0) {
                  const crs = new_obj.attributes_table.entries.map(o => o.level);
                  let copy_cr = Math.max(...crs.filter(o => o < new_level), -1);
                  if (copy_cr === -1) {
                    copy_cr = Math.min(...crs.filter(o => o > new_level), -1);
                  }
                  const entry = copy_cr === -1 ? new Attributes() : new_obj.attributes_table.entries.filter(o => o.level === copy_cr)[0].clone();
                  entry.level = new_level;
                  new_obj.attributes_table.entries.push(entry);
                }
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
                setAttributesLevel(new_level);
                setReloading(true);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>{ attributes_level > -1 && renderAttributes() }</Grid>
        </Grid>
      );
    } else if (tab === "resources") {
      const level_options = CHARACTER_LEVELS.map(o => +o).filter(o => o <= obj.max_level).map(o => { return `${o}`; });
      const use_entry = obj.resources_table.getEntry(resource_level);
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            size={{
              xs: 12
            }}>
            <TableInput
              obj={obj.resources_table}
              disabled={disabled}
              creature
              onChange={(changed: TableBase) => {
                const new_obj = obj;
                new_obj.resources_table.copyBase(changed);
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox 
              name="Select Level for Resources"
              options={level_options}
              value={`${resource_level}`}
              onChange={(changed: string) => {
                const new_obj = obj;
                const new_level = +changed;
                const old_entry = new_obj.resources_table.getEntry(new_level);
                if (!old_entry || old_entry.level !== new_level) {
                  const entry = old_entry ? old_entry.clone() : new TableEntry();
                  entry.level = new_level;
                  new_obj.resources_table.entries.push(entry);
                } else {
                  const entry = new TableEntry();
                  entry.level = new_level;
                  new_obj.resources_table.entries.push(entry);
                }
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
                setResourceLevel(new_level);
                setReloading(true);
              }}
            />
          </Grid>
          { use_entry && use_entry.level === resource_level &&
            <>
              <Grid
                size={{
                  xs: 10
                }}>
                <SelectObjectBox
                  name="New Resource"
                  data_type="resource"
                  extra_options={SLOT_LEVELS.filter(o => Object.keys(use_entry.data).filter(o2 => o2 === o).length === 0)}
                  filter={{ _id: { "$nin": Object.keys(use_entry.data) }}}
                  value=""
                  onChange={(changed: string) => {
                    const new_obj = obj;
                    const changing_entry = new_obj.resources_table.getEntry(resource_level);
                    if (changing_entry) {
                      changing_entry.data[changed] = 1;
                      if (!new_obj.resources_table.fields.includes(changed)) {
                        new_obj.resources_table.field_order[new_obj.resources_table.fields.length] = changed;
                      }
                      if (!changed.includes("Slot")) {
                        const resource = resources.find(o => o._id === changed);
                        if (resource && resource.default_dice_size !== 1) {
                          changing_entry.data[`${changed}_size`] = resource.default_dice_size;
                          if (!new_obj.resources_table.fields.includes(`${changed}_size`)) {
                            new_obj.resources_table.field_order[new_obj.resources_table.fields.length] = `${changed}_size`;
                          }
                        }
                      }
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                      setReloading(true);
                    }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 2
                }}>
                <DeleteButton
                  label="Delete Resource Level"
                  onClick={() => {
                    const new_obj = obj;
                    new_obj.resources_table.entries = new_obj.resources_table.entries.filter(o => o.level !== resource_level);
                    const other_crs = new_obj.resources_table.entries.map(o => o.level);
                    if (other_crs.length > 0) {
                      setResourceLevel(+other_crs[0]);
                    } else {
                      setResourceLevel(-1);
                    }
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                    setReloading(true);
                  }}
                />
              </Grid>
              { !reloading && 
                obj.resources_table.fields.filter(o => !o.endsWith("_size")).map((field, key) => {
                  const resource = field.includes("Slot") ? undefined : resources.find(o => o._id === field);
                  const count = use_entry.data[field];
                  const size = use_entry.data[`${field}_size`];
                  return (
                    <Grid
                      key={key}
                      container
                      spacing={0}
                      direction="row"
                      size={{
                        xs: 12
                      }}>
                      <Grid
                        size={{
                          xs: 4
                        }}>
                        { field.includes("Slot") ? field : 
                          <DisplayObjects type="resource" ids={[field]} />
                        }
                      </Grid>
                      <Grid
                        size={{
                          xs: 2
                        }}>
                        { resource && resource.default_dice_size !== 1 &&
                          <SelectStringBox
                            name="Dice Size"
                            options={DICE_SIZES}
                            value={`${size}`}
                            disabled={disabled}
                            onChange={(changed: string) => {
                              const new_obj = obj;
                              const new_entry = new_obj.resources_table.getEntry(resource_level);
                              if (new_entry) {
                                new_entry.data[`${field}_size`] = +changed;
                                setObj(new_obj);
                                setChangeTime(common_util.get_time_number());
                              }
                            }}
                          />
                        }
                      </Grid>
                      <Grid
                        size={{
                          xs: 3
                        }}>
                        <StringBox 
                          name="Count"
                          value={`${count}`}
                          type="number"
                          disabled={disabled}
                          onBlur={(changed: string) => {
                            const new_obj = obj;
                            const new_entry = new_obj.resources_table.getEntry(resource_level);
                            if (new_entry) {
                              new_entry.data[field] = +changed;
                              setObj(new_obj);
                              setChangeTime(common_util.get_time_number());
                            }
                          }}
                        />
                      </Grid>
                      <Grid
                        size={{
                          xs: 3
                        }}>
                        <DeleteButton
                          label="Remove Resource"
                          disabled={disabled}
                          onClick={() => {
                            const new_obj = obj;
                            const new_entry = new_obj.resources_table.getEntry(resource_level);
                            if (new_entry) {
                              delete new_entry.data[field];
                              delete new_entry.data[`${field}_size`];
                              const remove_field = new_obj.resources_table.entries.filter(o => o.data[field] !== undefined).length === 0;
                              if (remove_field) {
                                let indexes = Object.keys(new_obj.resources_table.field_order).map(o => +o).sort((a, b) => { return a - b; });
                                let found = false;
                                for (let i = 0; i < Math.max(...indexes); ++i) {
                                  if (found) {
                                    new_obj.resources_table.field_order[i - 1] = new_obj.resources_table.field_order[i];
                                  } else {
                                    if (new_obj.resources_table.field_order[i] === field) {
                                      found = true;
                                    }
                                  }
                                }
                                delete new_obj.resources_table.field_order[new_obj.resources_table.fields.length - 1];
                                if (new_obj.resources_table.fields.includes(`${field}_size`)) {
                                  indexes = Object.keys(new_obj.resources_table.field_order).map(o => +o).sort((a, b) => { return a - b; });
                                  found = false;
                                  for (let i = 0; i < Math.max(...indexes); ++i) {
                                    if (found) {
                                      new_obj.resources_table.field_order[i - 1] = new_obj.resources_table.field_order[i];
                                    } else {
                                      if (new_obj.resources_table.field_order[i] === `${field}_size`) {
                                        found = true;
                                      }
                                    }
                                  }
                                  delete new_obj.resources_table.field_order[new_obj.resources_table.fields.length - 1];
                                }
                              }
                              setObj(new_obj);
                              setChangeTime(common_util.get_time_number());
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                  );
                })
              }
            </>
          }
        </Grid>
      );
    } else if (tab === "actions") {
      if (expanded_feature_base) {
        // Creature FBs are simple, and only ever have one F, and the FB's name and description match the FF.
        const feature = expanded_feature_base.features[0];
        if (feature) {
          // const creature_instance = creature_instances.find(o => o.challenge_rating === expanded_feature_base.level);
          return (
            <div style={{ height: "100%", width: "calc(100% - 10px)" }}>
              <FeatureBaseInput
                parent_name={obj.name}
                feature_base={expanded_feature_base} 
                feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Actions")}
                feature_source={obj}
                disabled={disabled}
                // cfs={creature_instance}
                simple
                allowed_feature_types={CREATURE_FEATURE_TYPES}
                onChange={(changed: FeatureBase) => {
                  changed.creature_fb_type = "Actions";
                  const new_obj = obj;
                  let objFinder = new_obj.fbs.filter(o => o.id === changed.id);
                  if (objFinder.length === 1) {
                    const feature_base = objFinder[0];
                    feature_base.copy(changed);
                  } else {
                    if (common_util.unique_array(objFinder.map(o => 
                      o.features[0].floating_feature_id)).length === 1) {
                      // Something went wrong, causing it to duplicate one of them.  
                      // Delete them and then add changed.
                      new_obj.fbs = new_obj.fbs.filter(o => o.id !== changed.id);
                      new_obj.fbs.push(changed);
                    } else {
                      // Give one of them a new ID.  
                      // They probably have different levels, so use that.
                      objFinder = objFinder.filter(o => o.level === changed.level);
                      if (objFinder.length === 1) {
                        const feature_base = objFinder[0];
                        feature_base.copy(changed);
                        // Change the ids of the others
                        const others = new_obj.fbs.filter(o => o.id === changed.id && o.level !== changed.level);
                        for (const other of others) {
                          other.true_id = uuidv4().toString();
                        }
                      } else {
                        // Just pick one.
                        const feature_base = objFinder[0];
                        feature_base.copy(changed);
                        // Change the ids of the others
                        for (let i = 1; i < objFinder.length; ++i) {
                          const other = objFinder[i];
                          if (other) {
                            other.true_id = uuidv4().toString();
                          }
                        }
                      }
                    }
                  }
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
                onDelete={() => {
                  if (expanded_feature_base) {
                    const id = expanded_feature_base.id;
                    const new_obj = obj;
                    new_obj.fbs = new_obj.fbs.filter(o => o.id !== id);
                    setObj(new_obj);
                    setExpandedFeatureBase(null);
                    setChangeTime(common_util.get_time_number());
                  }
                }}
                onDone={() => {
                  setExpandedFeatureBase(null);
                }}
              />
            </div>
          );
        }
        return <>Error</>;
      } else {
        const height = window.innerHeight - 64;
        return (
          <FeatureBasesInput 
            feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Actions")} 
            feature_source={obj}
            // parent_id={myObj._id} 
            // parent_type="Race"
            disabled={disabled}
            onExpand={setExpandedFeatureBase}
            // creature_instances={creature_instances}
            level_filter={level_filter}
            onLevelFilterChange={setLevelFilter}
            height={height}
            onAdd={() => {
              const new_obj = obj;
              const new_fb = new FeatureBase();
              new_fb.creature_fb_type = "Actions";
              new_fb.level = level_filter > -1 ? level_filter : 1;
              new_fb.feature_source_id = new_obj._id;
              new_fb.feature_source_type = "creature";
              const new_f = new Feature();
              new_f.parent_type = new_fb.feature_source_type;
              new_f.parent_id = new_fb.feature_source_id;
              new_f.base_id = new_fb.id;
              new_fb.features.push(new_f);
              new_obj.fbs.push(new_fb);
              setObj(new_obj);
              setExpandedFeatureBase(new_fb);
              setChangeTime(common_util.get_time_number());
            }}
            onReorder={(updated) => {
              const new_obj = obj;
              for (const u of updated) {
                const fb = new_obj.fbs.find(o => o.true_id === u.true_id);
                if (fb) {
                  fb.copy(u);
                }
              }
              // new_obj.fbs = updated;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        );
      }
    } else if (tab === "legendary_actions") {
      if (expanded_feature_base) {
        // const creature_instance = creature_instances.find(o => o.challenge_rating === expanded_feature_base.level);
        return (
          <div style={{ height: "100%", width: "calc(100% - 10px)" }}>
            <FeatureBaseInput
              parent_name={obj.name}
              feature_base={expanded_feature_base} 
              feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Legendary Actions")}
              feature_source={obj}
              disabled={disabled}
              // cfs={creature_instance}
              simple
              allowed_feature_types={CREATURE_FEATURE_TYPES}
              onChange={(changed: FeatureBase) => {
                changed.creature_fb_type = "Legendary Actions";
                const new_obj = obj;
                let objFinder = new_obj.fbs.filter(o => o.id === changed.id);
                if (objFinder.length === 1) {
                  const feature_base = objFinder[0];
                  feature_base.copy(changed);
                } else {
                  if (common_util.unique_array(objFinder.map(o => 
                    o.features[0].floating_feature_id)).length === 1) {
                    // Something went wrong, causing it to duplicate one of them.  
                    // Delete them and then add changed.
                    new_obj.fbs = new_obj.fbs.filter(o => o.id !== changed.id);
                    new_obj.fbs.push(changed);
                  } else {
                    // Give one of them a new ID.  
                    // They probably have different levels, so use that.
                    objFinder = objFinder.filter(o => o.level === changed.level);
                    if (objFinder.length === 1) {
                      const feature_base = objFinder[0];
                      feature_base.copy(changed);
                      // Change the ids of the others
                      const others = new_obj.fbs.filter(o => o.id === changed.id && o.level !== changed.level);
                      for (const other of others) {
                        other.true_id = uuidv4().toString();
                      }
                    } else {
                      // Just pick one.
                      const feature_base = objFinder[0];
                      feature_base.copy(changed);
                      // Change the ids of the others
                      for (let i = 1; i < objFinder.length; ++i) {
                        const other = objFinder[i];
                        if (other) {
                          other.true_id = uuidv4().toString();
                        }
                      }
                    }
                  }
                }
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
              onDelete={() => {
                if (expanded_feature_base) {
                  const id = expanded_feature_base.id;
                  const new_obj = obj;
                  new_obj.fbs = new_obj.fbs.filter(o => o.id !== id);
                  setObj(new_obj);
                  setExpandedFeatureBase(null);
                  setChangeTime(common_util.get_time_number());
                }
              }}
              onDone={() => {
                setExpandedFeatureBase(null);
              }}
            />
          </div>
        );
      } else {
        const level_options = CHARACTER_LEVELS.map(o => +o).filter(o => o <= obj.max_level).map(o => { return `${o}`; });
        const use_entry = obj.legendaries_table.getEntry(legendaries_level);
        // console.log(use_entry);
        const height = window.innerHeight - 64;
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 12
              }}>
              <LegendariesTableInput
                obj={obj.legendaries_table}
                disabled={disabled}
                creature
                onChange={(changed: LegendariesTable) => {
                  const new_obj = obj;
                  new_obj.legendaries_table.copy(changed);
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 4
              }}>
              <SelectStringBox 
                name="Select Level for Legendaries"
                options={level_options}
                value={`${legendaries_level}`}
                onChange={(changed: string) => {
                  // debugger;
                  const new_obj = obj;
                  const new_level = +changed;
                  const old_entry = new_obj.legendaries_table.getEntry(new_level);
                  if (!old_entry || old_entry.level !== new_level) {
                    const entry = old_entry ? old_entry.clone() : new Legendaries();
                    entry.level = new_level;
                    if (entry.actions === undefined) {
                      entry.actions = 0;
                      entry.resistances = 0;
                    }
                    new_obj.legendaries_table.entries.push(entry);
                  } else {
                    const entry = new Legendaries();
                    entry.actions = 0;
                    entry.resistances = 0;
                    entry.level = new_level;
                    new_obj.legendaries_table.entries.push(entry);
                  }
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                  setLegendariesLevel(new_level);
                  setReloading(true);
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 4
              }}>
              { !reloading &&
                <StringBox
                  name="Legendary Actions"
                  value={ use_entry ? `${use_entry.actions}` : "0" }
                  type="number"
                  onChange={(changed: string) => {
                    const new_obj = obj;
                    const old_entry = new_obj.legendaries_table.entries.find(o => o.level === legendaries_level);
                    if (!old_entry) {
                      const entry = new Legendaries();
                      entry.level = legendaries_level;
                      entry.actions = +changed;
                      entry.resistances = 0;
                      new_obj.legendaries_table.entries.push(entry);
                    } else {
                      old_entry.actions = +changed;
                    }
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                    setReloading(true);
                  }}
                /> 
              }
            </Grid>
            <Grid
              size={{
                xs: 4
              }}>
              { !reloading &&
                <StringBox
                  name="Legendary Resistances"
                  value={ use_entry ? `${use_entry.resistances}` : "0" }
                  type="number"
                  onChange={(changed: string) => {
                    const new_obj = obj;
                    const old_entry = new_obj.legendaries_table.entries.find(o => o.level === legendaries_level);
                    if (!old_entry) {
                      const entry = new Legendaries();
                      entry.level = legendaries_level;
                      entry.actions = 0;
                      entry.resistances = +changed;
                      new_obj.legendaries_table.entries.push(entry);
                    } else {
                      old_entry.resistances = +changed;
                    }
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                    setReloading(true);
                  }}
                />
              }
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              <FeatureBasesInput 
                feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Legendary Actions")} 
                feature_source={obj}
                // parent_id={myObj._id} 
                // parent_type="Race"
                disabled={disabled}
                onExpand={setExpandedFeatureBase}
                // creature_instances={creature_instances}
                level_filter={level_filter}
                onLevelFilterChange={setLevelFilter}
                height={height}
                onAdd={() => {
                  const new_obj = obj;
                  const new_fb = new FeatureBase();
                  new_fb.creature_fb_type = "Legendary Actions";
                  new_fb.level = level_filter > -1 ? level_filter : 1;
                  new_fb.feature_source_id = new_obj._id;
                  new_fb.feature_source_type = "creature";
                  const new_f = new Feature();
                  new_f.parent_type = new_fb.feature_source_type;
                  new_f.parent_id = new_fb.feature_source_id;
                  new_f.base_id = new_fb.id;
                  new_fb.features.push(new_f);
                  new_obj.fbs.push(new_fb);
                  setObj(new_obj);
                  setExpandedFeatureBase(new_fb);
                  setChangeTime(common_util.get_time_number());
                }}
                onReorder={(updated) => {
                  const new_obj = obj;
                  for (const u of updated) {
                    const fb = new_obj.fbs.find(o => o.true_id === u.true_id);
                    if (fb) {
                      fb.copy(u);
                    }
                  }
                  // new_obj.fbs = updated;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
          </Grid>
        );
      }
    } else if (tab === "lair_actions") {
      if (expanded_feature_base) {
        // const creature_instance = creature_instances.find(o => o.challenge_rating === expanded_feature_base.level);
        return (
          <div style={{ height: "100%", width: "calc(100% - 10px)" }}>
            <FeatureBaseInput
              parent_name={obj.name}
              feature_base={expanded_feature_base} 
              feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Lair Actions")}
              feature_source={obj}
              disabled={disabled}
              // cfs={creature_instance}
              simple
              allowed_feature_types={CREATURE_FEATURE_TYPES}
              onChange={(changed: FeatureBase) => {
                changed.creature_fb_type = "Lair Actions";
                const new_obj = obj;
                let objFinder = new_obj.fbs.filter(o => o.id === changed.id);
                if (objFinder.length === 1) {
                  const feature_base = objFinder[0];
                  feature_base.copy(changed);
                } else {
                  if (common_util.unique_array(objFinder.map(o => 
                    o.features[0].floating_feature_id)).length === 1) {
                    // Something went wrong, causing it to duplicate one of them.  
                    // Delete them and then add changed.
                    new_obj.fbs = new_obj.fbs.filter(o => o.id !== changed.id);
                    new_obj.fbs.push(changed);
                  } else {
                    // Give one of them a new ID.  
                    // They probably have different levels, so use that.
                    objFinder = objFinder.filter(o => o.level === changed.level);
                    if (objFinder.length === 1) {
                      const feature_base = objFinder[0];
                      feature_base.copy(changed);
                      // Change the ids of the others
                      const others = new_obj.fbs.filter(o => o.id === changed.id && o.level !== changed.level);
                      for (const other of others) {
                        other.true_id = uuidv4().toString();
                      }
                    } else {
                      // Just pick one.
                      const feature_base = objFinder[0];
                      feature_base.copy(changed);
                      // Change the ids of the others
                      for (let i = 1; i < objFinder.length; ++i) {
                        const other = objFinder[i];
                        if (other) {
                          other.true_id = uuidv4().toString();
                        }
                      }
                    }
                  }
                }
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
              onDelete={() => {
                if (expanded_feature_base) {
                  const id = expanded_feature_base.id;
                  const new_obj = obj;
                  new_obj.fbs = new_obj.fbs.filter(o => o.id !== id);
                  setObj(new_obj);
                  setExpandedFeatureBase(null);
                  setChangeTime(common_util.get_time_number());
                }
              }}
              onDone={() => {
                setExpandedFeatureBase(null);
              }}
            />
          </div>
        );
      } else {
        const height = window.innerHeight - 64;
        return (
          <FeatureBasesInput 
            feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Lair Actions")} 
            feature_source={obj}
            // parent_id={myObj._id} 
            // parent_type="Race"
            disabled={disabled}
            onExpand={setExpandedFeatureBase}
            // creature_instances={creature_instances}
            level_filter={level_filter}
            onLevelFilterChange={setLevelFilter}
            height={height}
            onAdd={() => {
              const new_obj = obj;
              const new_fb = new FeatureBase();
              new_fb.creature_fb_type = "Lair Actions";
              new_fb.level = level_filter > -1 ? level_filter : 1;
              new_fb.feature_source_id = new_obj._id;
              new_fb.feature_source_type = "creature";
              const new_f = new Feature();
              new_f.parent_type = new_fb.feature_source_type;
              new_f.parent_id = new_fb.feature_source_id;
              new_f.base_id = new_fb.id;
              new_fb.features.push(new_f);
              new_obj.fbs.push(new_fb);
              setObj(new_obj);
              setExpandedFeatureBase(new_fb);
              setChangeTime(common_util.get_time_number());
            }}
            onReorder={(updated) => {
              const new_obj = obj;
              for (const u of updated) {
                const fb = new_obj.fbs.find(o => o.true_id === u.true_id);
                if (fb) {
                  fb.copy(u);
                }
              }
              // new_obj.fbs = updated;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        );
      }
    } else if (tab === "traits") {
      if (expanded_feature_base) {
        return (
          <div style={{ height: "100%", width: "calc(100% - 10px)" }}>
            <FeatureBaseInput
              parent_name={obj.name}
              feature_base={expanded_feature_base} 
              feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Traits")}
              feature_source={obj}
              disabled={disabled}
              simple
              allowed_feature_types={CREATURE_FEATURE_TYPES}
              onChange={(changed: FeatureBase) => {
                changed.creature_fb_type = "Traits";
                const new_obj = obj;
                let objFinder = new_obj.fbs.filter(o => o.id === changed.id);
                if (objFinder.length === 1) {
                  const feature_base = objFinder[0];
                  feature_base.copy(changed);
                } else {
                  if (common_util.unique_array(objFinder.map(o => 
                    o.features[0].floating_feature_id)).length === 1) {
                    // Something went wrong, causing it to duplicate one of them.  
                    // Delete them and then add changed.
                    new_obj.fbs = new_obj.fbs.filter(o => o.id !== changed.id);
                    new_obj.fbs.push(changed);
                  } else {
                    // Give one of them a new ID.  
                    // They probably have different levels, so use that.
                    objFinder = objFinder.filter(o => o.level === changed.level);
                    if (objFinder.length === 1) {
                      const feature_base = objFinder[0];
                      feature_base.copy(changed);
                      // Change the ids of the others
                      const others = new_obj.fbs.filter(o => o.id === changed.id && o.level !== changed.level);
                      for (const other of others) {
                        other.true_id = uuidv4().toString();
                      }
                    } else {
                      // Just pick one.
                      const feature_base = objFinder[0];
                      feature_base.copy(changed);
                      // Change the ids of the others
                      for (let i = 1; i < objFinder.length; ++i) {
                        const other = objFinder[i];
                        if (other) {
                          other.true_id = uuidv4().toString();
                        }
                      }
                    }
                  }
                }
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
              onDelete={() => {
                if (expanded_feature_base) {
                  const id = expanded_feature_base.id;
                  const new_obj = obj;
                  new_obj.fbs = new_obj.fbs.filter(o => o.id !== id);
                  setObj(new_obj);
                  setExpandedFeatureBase(null);
                  setChangeTime(common_util.get_time_number());
                }
              }}
              onDone={() => {
                setExpandedFeatureBase(null);
              }}
            />
          </div>
        );
      } else {
        const height = window.innerHeight - 64;
        return (
          <FeatureBasesInput 
            feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Traits")} 
            feature_source={obj}
            disabled={disabled}
            onExpand={setExpandedFeatureBase}
            // creature_instances={creature_instances}
            level_filter={level_filter}
            onLevelFilterChange={setLevelFilter}
            height={height}
            onAdd={() => {
              const new_obj = obj;
              const new_fb = new FeatureBase();
              new_fb.creature_fb_type = "Traits";
              new_fb.level = level_filter > -1 ? level_filter : 1;
              new_fb.feature_source_id = new_obj._id;
              new_fb.feature_source_type = "creature";
              const new_f = new Feature();
              new_f.parent_type = new_fb.feature_source_type;
              new_f.parent_id = new_fb.feature_source_id;
              new_f.base_id = new_fb.id;
              new_fb.features.push(new_f);
              new_obj.fbs.push(new_fb);
              setObj(new_obj);
              setExpandedFeatureBase(new_fb);
              setChangeTime(common_util.get_time_number());
            }}
            onReorder={(updated) => {
              const new_obj = obj;
              for (const u of updated) {
                const fb = new_obj.fbs.find(o => o.true_id === u.true_id);
                if (fb) {
                  fb.copy(u);
                }
              }
              // new_obj.fbs = updated;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        );
      }
    } else if (tab === "spells") {
      return (
        <CreatureSpells
          obj={obj}
          disabled={disabled}
          onChange={() => {
            const new_obj = obj;
            setObj(new_obj);
          }}
        />
      ); 
    } else if (tab === "table") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            size={{
              xs: 12
            }}>
            <TableInput
              obj={obj.table}
              disabled={disabled}
              fields={[...ABILITY_SCORES,"AC","Size","Hit Dice Count","CR"]}
              creature
              onChange={(changed: TableBase) => {
                const new_obj = obj;
                new_obj.table.copyBase(changed);
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
        </Grid>
      ); 
    } else {
      return (
        <Grid container spacing={0} direction="row">
          <Grid size={4}>
            <StringBox 
              name="Name"
              value={obj.name} 
              message={obj.name.length > 0 ? "" : "Name Invalid"} 
              disabled={disabled}
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.name = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid size={4}>
            <StringBox 
              name="Name Formula"
              value={obj.name_formula} 
              disabled={disabled}
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.name_formula = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid size={4}>
            <SelectStringBox 
              name="Max Level"
              options={CHARACTER_LEVELS}
              value={`${obj.max_level}`} 
              disabled={disabled}
              onChange={(changed: string) => {
                const new_obj = obj;
                new_obj.max_level = +changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid size={4}>
            <SelectStringBox 
              name="Override Creature Type" 
              options={ CREATURE_TYPES }
              extra_options={["None"]}
              value={obj.override_type} 
              disabled={disabled}
              onChange={(changed: CreatureType) => {
                const new_obj = obj;
                new_obj.override_type = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }} 
            />
          </Grid>
          <Grid size={4}>
            <SuggestionStringBox 
              name="Add Subtypes"
              field="subtypes"
              values={obj.bonus_subtypes}
              disabled={disabled}
              multiple
              onChange={(changed: string[]) => {
                const new_obj = obj;
                new_obj.bonus_subtypes = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid size={4}>
            <SuggestionStringBox 
              name="Remove Subtypes"
              field="subtypes"
              values={obj.remove_subtypes}
              disabled={disabled}
              multiple
              onChange={(changed: string[]) => {
                const new_obj = obj;
                new_obj.remove_subtypes = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          
          <Grid size={4}>
            <SelectStringBox
              name="Hit Dice Count Replacer Type"
              value={obj.replace_hd_count_type}
              options={["Replace", "Replace if Greater"]}
              disabled={disabled}
              onChange={(changed: "Replace" | "Replace if Greater") => {
                const new_obj = obj;
                new_obj.replace_hd_count_type = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid size={4}>
            <UpgradableNumberBox 
              name="Replace Hit Dice Count"
              value={obj.replace_hd_count}
              disabled={disabled}
              onChange={(changed) => {
                const new_obj = obj;
                new_obj.replace_hd_count = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid size={4}>
            <UpgradableNumberBox 
              name="Extra Hit Dice Count"
              value={obj.extra_hd_count}
              disabled={disabled}
              onChange={(changed) => {
                const new_obj = obj;
                new_obj.extra_hd_count = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          
          <Grid size={12}>
            <ModelBaseDetails
              obj={obj}
              no_grid
              disabled={disabled}
              change_time={change_time}
              onChange={(changed: ModelBase) => {
                if (changed instanceof CreatureGift) {
                  const new_obj = obj;
                  new_obj.copy(changed);
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }
              }}
              get_creature_homebrewery_string={get_creature_homebrewery_string}>
              <Grid container spacing={0} direction="row">
                <Grid size={12}>
                  <DamageTypeOverridesInput 
                    obj={obj} 
                    disabled={disabled}
                    onChange={(changed: CreatureGift) => {
                      const new_obj = obj;
                      new_obj.copy(changed);
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}              
                  />
                </Grid>
                
                <Grid size={12}>
                  <CheckBox 
                    name="For Enhance Creature Ability Effect"
                    value={obj.enhance_creature} 
                    disabled={disabled}
                    onChange={(changed) => {
                      const new_obj = obj;
                      new_obj.enhance_creature = changed;
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}              
                  />
                </Grid>
                <Grid size={12} className="header">Creature Limits</Grid>
                <Grid size={12}>
                  <CreatureLimitInput
                    obj={obj.limit}
                    change_time={change_time}
                    disabled={disabled}
                    onChange={(changed: CreatureLimitation) => {
                      const new_obj = obj;
                      new_obj.limit.copy(changed);
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}
                  />
                </Grid>
                <Grid size={12}>
                  <StringsBox
                    name="Tags"
                    values={obj.tags}
                    disabled={disabled}
                    onChange={(changed: string[]) => {
                      const new_obj = obj;
                      new_obj.tags = changed;
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}
                  />
                </Grid>
              </Grid>
            </ModelBaseDetails>
          </Grid>
        </Grid>
      ); 
    }
  }

  const get_creature_homebrewery_string = (ci: BasicCreatureInstance | undefined = undefined) => {
    const creature = obj;
    const str = `# ${ci ? ci.name : creature.true_name}\n\r___\n\r`;
    // let ac = "See Properties Table";
    // let hp = "See Properties Table";
    // let ability_scores_string = "";
    // let saving_throws = "";
    // let skills = "";
    // let senses_string = "";
    // let tools = "";
    // let immunities_string = "";
    // let resistances_string = "";
    // let vulnerabilities_string = "";
    // let condition_immunities_string = "";
    // let languages = "";
    // let cr_string = "";
    // let prof_bonus = "See Properties Table";
    // let table_string = "";
    // let resource_table_string = "";
    // let legendaries_table_string = "";
    // let attributes_table_string = "";
    return str;
  }

  const renderAttributes = () => {
    const use_entry = obj.attributes_table.entries.find(o => o.level === attributes_level);
    if (use_entry) {
      if (edit_attribute === "speed") {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              style={{ cursor: "pointer", fontWeight: "bold" }}
              onClick={() => {
                setEditAttribute("");
              }}
              size={{
                xs: 2
              }}>
              Back
            </Grid>
            <Grid
              size={{
                xs: 10
              }}>
              Speed
            </Grid>
            { renderSpeeds(use_entry) }
          </Grid>
        );
      } else if (edit_attribute === "senses") {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              style={{ cursor: "pointer", fontWeight: "bold" }}
              onClick={() => {
                setEditAttribute("");
              }}
              size={{
                xs: 2
              }}>
              Back
            </Grid>
            <Grid
              size={{
                xs: 10
              }}>
              Senses
            </Grid>
            { renderSenses(use_entry) }
          </Grid>
        );
      } else if (edit_attribute === "condition_immunities") {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              style={{ cursor: "pointer", fontWeight: "bold" }}
              onClick={() => {
                setEditAttribute("");
              }}
              size={{
                xs: 2
              }}>
              Back
            </Grid>
            <Grid
              size={{
                xs: 10
              }}>
              Condition Immunities
            </Grid>
            { conditions && conditions.filter(o => o.common).map(condition => {
              return renderCondition(use_entry, condition);
            })}
          </Grid>
        );
      } else if (edit_attribute === "immunities") {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              style={{ cursor: "pointer", fontWeight: "bold" }}
              onClick={() => {
                setEditAttribute("");
              }}
              size={{
                xs: 2
              }}>
              Back
            </Grid>
            <Grid
              size={{
                xs: 10
              }}>
              Damage Immunities
            </Grid>
            { DAMAGE_TYPES.map(damage_type => {
              return renderDamageType(use_entry, damage_type, 0);
            })}
            <Grid
              size={{
                xs: 12
              }}>
              <StringBox
                name="Extra Damage Immunities"
                value={ use_entry.extra_damage_immunities }
                disabled={disabled}
                onBlur={(changed: string) => {
                  const new_obj = obj;
                  use_entry.extra_damage_immunities = changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
          </Grid>
        );
      } else if (edit_attribute === "resistances") {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              style={{ cursor: "pointer", fontWeight: "bold" }}
              onClick={() => {
                setEditAttribute("");
              }}
              size={{
                xs: 2
              }}>
              Back
            </Grid>
            <Grid
              size={{
                xs: 10
              }}>
              Damage Resistances
            </Grid>
            { DAMAGE_TYPES.map(damage_type => {
              return renderDamageType(use_entry, damage_type, 0.5);
            })}
            <Grid
              size={{
                xs: 12
              }}>
              <StringBox
                name="Extra Damage Resistances"
                value={ use_entry.extra_damage_resistances }
                disabled={disabled}
                onBlur={(changed: string) => {
                  const new_obj = obj;
                  use_entry.extra_damage_resistances = changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
          </Grid>
        );
      } else if (edit_attribute === "vulnerabilities") {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              style={{ cursor: "pointer", fontWeight: "bold" }}
              onClick={() => {
                setEditAttribute("");
              }}
              size={{
                xs: 2
              }}>
              Back
            </Grid>
            <Grid
              size={{
                xs: 10
              }}>
              Damage Vulnerabilities
            </Grid>
            { DAMAGE_TYPES.map(damage_type => {
              return renderDamageType(use_entry, damage_type, 2);
            })}
            <Grid
              size={{
                xs: 12
              }}>
              <StringBox
                name="Extra Damage Vulnerabilities"
                value={ use_entry.extra_damage_vulnerabilities }
                disabled={disabled}
                onBlur={(changed: string) => {
                  const new_obj = obj;
                  use_entry.extra_damage_vulnerabilities = changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
          </Grid>
        );
      } else if (edit_attribute === "tools") {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              style={{ cursor: "pointer", fontWeight: "bold" }}
              onClick={() => {
                setEditAttribute("");
              }}
              size={{
                xs: 2
              }}>
              Back
            </Grid>
            <Grid
              size={{
                xs: 10
              }}>
              Tools
            </Grid>
            { tools && tools.map(tool => {
              return renderTool(use_entry, tool);
            })}
          </Grid>
        );
      } else if (edit_attribute === "skills") {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              style={{ cursor: "pointer", fontWeight: "bold" }}
              onClick={() => {
                setEditAttribute("");
              }}
              size={{
                xs: 2
              }}>
              Back
            </Grid>
            <Grid
              size={{
                xs: 10
              }}>
              Skills
            </Grid>
            { SKILLS.sort((a,b) => a.name.localeCompare(b.name)).map(skill => {
              return renderSkill(use_entry, skill);
            })}
          </Grid>
        );
      } else if (edit_attribute === "saving_throws") {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              style={{ cursor: "pointer", fontWeight: "bold" }}
              onClick={() => {
                setEditAttribute("");
              }}
              size={{
                xs: 2
              }}>
              Back
            </Grid>
            <Grid
              size={{
                xs: 10
              }}>
              Saving Throws
            </Grid>
            { renderSavingThrow(use_entry, "STR") }
            { renderSavingThrow(use_entry, "DEX") }
            { renderSavingThrow(use_entry, "CON") }
            { renderSavingThrow(use_entry, "INT") }
            { renderSavingThrow(use_entry, "WIS") }
            { renderSavingThrow(use_entry, "CHA") }
          </Grid>
        );
      } else if (edit_attribute === "languages") {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              style={{ cursor: "pointer", fontWeight: "bold" }}
              onClick={() => {
                setEditAttribute("");
              }}
              size={{
                xs: 2
              }}>
              Back
            </Grid>
            <Grid
              size={{
                xs: 10
              }}>
              Languages
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              <StringBox
                name="Languages"
                value={use_entry.languages}
                disabled={disabled}
                onBlur={(changed: string) => {
                  const new_obj = obj;
                  use_entry.languages = changed;
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
          </Grid>
        );
      } else {
        return (
          <Grid container spacing={0} direction="column" style={{ cursor: "pointer" }}>
            <Grid
              onClick={() => {
                setEditAttribute("speed");
              }}>
              <b>Speed</b> { use_entry.speed_string }
            </Grid>
            <Grid
              onClick={() => {
                setEditAttribute("senses");
              }}>
              <b>Senses</b> { use_entry.senses_string }
            </Grid>
            <Grid
              onClick={() => {
                setEditAttribute("immunities");
              }}>
              <b>Damage Immunities</b> { use_entry.immunities_string }
            </Grid>
            <Grid
              onClick={() => {
                setEditAttribute("resistances");
              }}>
              <b>Damage Resistances</b> { use_entry.resistances_string }
            </Grid>
            <Grid
              onClick={() => {
                setEditAttribute("vulnerabilities");
              }}>
              <b>Damage Vulnerabilities</b> { use_entry.vulnerabilities_string }
            </Grid>
            <Grid
              onClick={() => {
                setEditAttribute("condition_immunities");
              }}>
              <b>Condition Immunities</b> { use_entry.condition_immunities_string }
            </Grid>
            <Grid
              onClick={() => {
                setEditAttribute("tools");
              }}>
              <b>Tools</b> { use_entry.tools_string }
            </Grid>
            <Grid
              onClick={() => {
                setEditAttribute("skills");
              }}>
              <b>Skills</b> { use_entry.skills_string }
            </Grid>
            <Grid
              onClick={() => {
                setEditAttribute("saving_throws");
              }}>
              <b>Saving Throws</b> { use_entry.saving_throws_string }
            </Grid>
            <Grid
              onClick={() => {
                setEditAttribute("languages");
              }}>
              <b>Languages</b> { use_entry.languages }
            </Grid>
          </Grid>
        );
      }
    }
  }

  const renderSpeeds = (use_entry: Attributes) => {
    // const disabled = api.user === null || (!api.user.admin && obj.owner_id !== api.user._id);
    const return_me: any[] = [];
    return_me.push(renderSpeed(use_entry, "walk", "Walk (in feet)"));
    return_me.push(renderSpeed(use_entry, "swim", "Swim (in feet)"));
    return_me.push(renderSpeed(use_entry, "climb", "Climb (in feet)"));
    return_me.push(renderSpeed(use_entry, "fly", "Fly (in feet)"));
    return_me.push(renderSpeed(use_entry, "burrow", "Burrow (in feet)"));
    return_me.push(  
      <Grid
        key="hover"
        size={{
          xs: 4
        }}>
        <ToggleButtonBox
          name="Hover"
          value={ use_entry.hover }
          disabled={disabled}
          onToggle={() => {
            const new_obj = obj;
            use_entry.hover = !use_entry.hover;
            setObj(new_obj);
            setChangeTime(common_util.get_time_number());
          }}
        />
      </Grid>
    );
    return return_me;
  }

  const renderSpeed = (use_entry: Attributes, name: string, display: string) => {
    const speed = use_entry.speeds[name];
    return (
      <Grid
        key={name}
        size={{
          xs: 4
        }}>
        <StringBox
          name={ display }
          value={ speed && speed > 0 ? `${speed}` : "" }
          type="number"
          disabled={disabled}
          onBlur={(changed: string) => {
            const new_obj = obj;
            if (changed === "") {
              use_entry.speeds[name] = 0;
            } else {
              use_entry.speeds[name] = +changed;
            }
            setObj(new_obj);
            setChangeTime(common_util.get_time_number());
          }}
        />
      </Grid>
    );
  }

  const renderSenses = (use_entry: Attributes) => {
    const return_me: any[] = [];
    for (const sense of senses) {
      return_me.push(renderSense(use_entry, sense.name));
    }
    return return_me;
  }

  const renderSense = (use_entry: Attributes, name: string) => {
    const range = use_entry.senses[name];
    return (
      <Grid
        key={name}
        size={{
          xs: 4
        }}>
        <StringBox
          name={ `${name} (in feet)` }
          value={ range ? `${range}` : "" }
          type="number"
          disabled={disabled}
          onBlur={(changed: string) => {
            const new_obj = obj;
            if (changed === "" || changed === "0") {
              delete use_entry.senses[name];
            } else {
              use_entry.senses[name] = +changed;
            }
            setObj(new_obj);
            setChangeTime(common_util.get_time_number());
          }}
        />
      </Grid>
    );
  }
  
  const renderSkill = (use_entry: Attributes, skill: Skill) => {
    // if (skill.name === "Arcana") {
    //   // console.log(skill);
    //   // console.log(obj.skill_proficiencies2[skill._id]);
    // }
    return (
      <Grid
        key={skill._id}
        size={{
          xs: 4
        }}>
        <SelectStringBox
          name={ skill.name }
          value={ use_entry.skill_profs[skill._id] ? `${use_entry.skill_profs[skill._id]}` : "0" }
          options={PROFICIENCY_OPTIONS}
          disabled={disabled}
          onChange={(changed: string) => {
            const new_obj = obj;
            if (changed === "0") {
              delete use_entry.skill_profs[skill._id];
            } else {
              use_entry.skill_profs[skill._id] = +changed;
            }
            setObj(new_obj);
            setChangeTime(common_util.get_time_number());
          }}
        />
      </Grid>
    );
  }
  
  const renderSavingThrow = (use_entry: Attributes, ability_score: AbilityScoreType) => {
    return (
      <Grid
        size={{
          xs: 4
        }}>
        <CheckBox
          name={ ability_score }
          value={ use_entry.save_profs.includes(ability_score) }
          disabled={disabled}
          onChange={(changed: boolean) => {
            const new_obj = obj;
            if (changed) {
              use_entry.save_profs.push(ability_score);
            } else {
              use_entry.save_profs = use_entry.save_profs.filter(o => o !== ability_score);
            }
            setObj(new_obj);
            setChangeTime(common_util.get_time_number());
          }}
        />
      </Grid>
    );
  }
  
  const renderTool = (use_entry: Attributes, tool: Tool) => {
    return (
      <Grid
        key={tool._id}
        container
        spacing={0}
        direction="row"
        size={{
          xs: 4
        }}>
        <ToggleButtonBox
          name={ tool.name }
          disabled={disabled}
          value={ use_entry.tool_profs.includes(tool.name) }
          onToggle={() => {
            const new_obj = obj;
            if (use_entry.tool_profs.includes(tool.name)) {
              use_entry.tool_profs = use_entry.tool_profs.filter(o => o !== tool.name);
            } else {
              use_entry.tool_profs.push(tool.name);
            }
            setObj(new_obj);
            setChangeTime(common_util.get_time_number());
          }}
        />
      </Grid>
    );
  }
  
  const renderCondition = (use_entry: Attributes, condition: Condition) => {
    return (
      <Grid
        key={condition._id}
        container
        spacing={0}
        direction="row"
        size={{
          xs: 4
        }}>
        <ToggleButtonBox
          name={ condition.name }
          disabled={disabled}
          value={ use_entry.condition_immunities.includes(condition.name) }
          onToggle={() => {
            const new_obj = obj;
            if (use_entry.condition_immunities.includes(condition.name)) {
              use_entry.condition_immunities = use_entry.condition_immunities.filter(o => o !== condition.name);
            } else {
              use_entry.condition_immunities.push(condition.name);
            }
            setObj(new_obj);
            setChangeTime(common_util.get_time_number());
          }}
        />
      </Grid>
    );
  }
  
  const renderDamageType = (use_entry: Attributes, damage_type: DamageType, multiplier: number) => {
    const damage_multipliers = multiplier === 0 ? use_entry.damage_immunities : (multiplier === 0.5 ? use_entry.damage_resistances : use_entry.damage_vulnerabilities);
    return (
      <Grid
        key={damage_type}
        container
        spacing={0}
        direction="row"
        size={{
          xs: 4
        }}>
        <ToggleButtonBox
          name={ damage_type }
          disabled={disabled}
          value={ damage_multipliers.includes(damage_type) }
          onToggle={() => {
            const new_obj = obj;
            let damage_types = multiplier === 0 ? use_entry.damage_immunities : (multiplier === 0.5 ? use_entry.damage_resistances : use_entry.damage_vulnerabilities);
            if (damage_types.includes(damage_type)) {
              damage_types = damage_types.filter(o => o !== damage_type);
            } else {
              damage_types.push(damage_type);
            }
            if (multiplier === 0) {
              use_entry.damage_immunities = damage_types;
            } else if (multiplier === 0.5) {
              use_entry.damage_resistances = damage_types;
            } else {
              use_entry.damage_vulnerabilities = damage_types;
            }
            setObj(new_obj);
            setChangeTime(common_util.get_time_number());
          }}
        />
      </Grid>
    );
  }

  if (!fetched || loading) {
    const height = window.innerHeight - 81;
    return (<LoadingBox size={height * 0.5} height={height} width={window.innerWidth - 1} />);
  } else if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else { 
    const menu_options: IStringHash = disabled ? {
      "Home": "main",
      // "HP": "hp",
      "Attributes": "attributes",
      // "Ability Scores": "ability_scores",
      "Resources": "resources",
      "Actions": "actions",
      "Legendary Actions": "legendary_actions",
      "Lair Actions": "lair_actions",
      "Traits": "traits",
      "Spells": "spells",
      "Table": "table"
    } : {
      "Home": "main",
      // "HP": "hp",
      "Attributes": "attributes",
      // "More Attributes": "more_attributes",
      // "Even More Attributes": "even_more_attributes",
      // "Ability Scores": "ability_scores",
      "Resources": "resources",
      "Actions": "actions",
      "Legendary Actions": "legendary_actions",
      "Lair Actions": "lair_actions",
      "Traits": "traits",
      "Spells": "spells",
      "Table": "table",
      // "Parse": "parse",
    };
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={12}>
          <Tooltip title={`Back to Creature Gifts`}>
            <Fab size="small" color="primary" style={{marginLeft: "8px"}}
              onClick={ () => {
                setNavigateTo(`/creature_gift`);
              }}>
              <ArrowBack/>
            </Fab>
          </Tooltip>&nbsp;
          <span className="header">
            { obj._id === "" ? "Create Creature Gift" : `Edit ${obj.name}` }
          </span>
        </Grid>
        { !disabled &&
          <Grid size={12}>
            <TemplateBox
              obj={obj}
              type="Creature Gift"
              useTemplate={(template: TemplateBase) => {
                const new_obj = obj;
                new_obj.copyTemplate(template);
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
        }
        <Grid size={12}>
          <FloatMenu
            option_map={menu_options}
            selected={tab}
            onSelect={(changed: string) => {
              // if (changed.includes("actions") || changed === "traits") {
              //   updateCreatureInstance();
              // }
              setTab(changed);
            }}
          />
        </Grid>
        <Grid size={12}>
          { render_tab() }
        </Grid>
      </Grid>
    ); 
  }
}
